import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Button,
  Typography,
  useTheme
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LanguageIcon from '@mui/icons-material/Language';

interface Language {
  code: string;
  name: string;
  nativeName: string;
}

const languages: Language[] = [
  { code: 'en', name: 'English', nativeName: 'English' },
  { code: 'ur', name: 'Urdu', nativeName: 'اردو' },
  { code: 'fr', name: 'French', nativeName: 'Français' },
  { code: 'ar', name: 'Arabic', nativeName: 'العربية' },
];

interface LanguageSelectorPopupProps {
  open: boolean;
  onClose: () => void;
}

const LanguageSelectorPopup: React.FC<LanguageSelectorPopupProps> = ({ open, onClose }) => {
  const { i18n, t } = useTranslation();
  const theme = useTheme();
  const [currentLanguage, setCurrentLanguage] = useState<string>(i18n.language || 'en');

  useEffect(() => {
    setCurrentLanguage(i18n.language || 'en');
  }, [i18n.language]);

  const handleLanguageChange = (langCode: string) => {
    setCurrentLanguage(langCode);
    i18n.changeLanguage(langCode);
    
    // Update document direction for RTL languages
    if (langCode === 'ar' || langCode === 'ur') {
      document.documentElement.setAttribute('dir', 'rtl');
    } else {
      document.documentElement.setAttribute('dir', 'ltr');
    }
    
    // Close popup after a short delay
    setTimeout(() => {
      onClose();
    }, 200);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        }
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LanguageIcon color="primary" />
          <Typography variant="h6" component="span">
            {t('language.selectLanguage')}
          </Typography>
        </Box>
        <IconButton
          aria-label="close"
          onClick={onClose}
          size="small"
          sx={{
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pb: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {languages.map((lang) => {
            const isSelected = currentLanguage === lang.code;
            return (
              <Button
                key={lang.code}
                onClick={() => handleLanguageChange(lang.code)}
                variant={isSelected ? 'contained' : 'outlined'}
                fullWidth
                sx={{
                  py: 1.5,
                  px: 2,
                  justifyContent: 'space-between',
                  textTransform: 'none',
                  borderColor: isSelected ? 'primary.main' : 'divider',
                  backgroundColor: isSelected ? 'primary.main' : 'transparent',
                  color: isSelected ? 'primary.contrastText' : 'text.primary',
                  '&:hover': {
                    backgroundColor: isSelected ? 'primary.dark' : 'action.hover',
                    borderColor: isSelected ? 'primary.dark' : 'primary.main',
                  },
                }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: isSelected ? 600 : 400,
                      color: isSelected ? 'inherit' : 'text.primary',
                    }}
                  >
                    {lang.nativeName}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: isSelected ? 'rgba(255, 255, 255, 0.7)' : 'text.secondary',
                      fontSize: '0.75rem',
                    }}
                  >
                    {lang.name}
                  </Typography>
                </Box>
                {isSelected && (
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      backgroundColor: 'primary.contrastText',
                    }}
                  />
                )}
              </Button>
            );
          })}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default LanguageSelectorPopup;
